/**
 * Cursor Workspace Generator Test
 * Tests Cursor URL protocol and dynamic workspace generation
 *
 * Access: http://flownotes.localhost/?test=cursor-workspace
 */

import { useState } from 'react';
import { ExternalLink, Copy, CheckCircle, XCircle, AlertCircle, Terminal, FileCode, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface TestResult {
  test: string;
  status: 'pending' | 'success' | 'failed' | 'unknown';
  message: string;
  command?: string;
}

export function TestCursorWorkspace() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [generatedWorkspace, setGeneratedWorkspace] = useState<string | null>(null);

  const updateResult = (test: string, status: TestResult['status'], message: string, command?: string) => {
    setResults(prev => {
      const filtered = prev.filter(r => r.test !== test);
      return [...filtered, { test, status, message, command }];
    });
  };

  // Test 1: Open README file
  const testOpenFile = () => {
    const fileUrl = 'cursor://file/Users/rowan/Documents/flownotes/README.md';
    updateResult('open-file', 'pending', 'Opening README.md in Cursor...');
    
    window.location.href = fileUrl;
    
    // Can't detect success automatically - user must confirm
    setTimeout(() => {
      updateResult(
        'open-file',
        'unknown',
        'Check if Cursor opened README.md',
        fileUrl
      );
    }, 1000);
  };

  // Test 2: Open workspace file
  const testOpenWorkspace = () => {
    const workspaceUrl = 'cursor://file/Users/rowan/Documents/flownotes/flownotes.code-workspace';
    updateResult('open-workspace', 'pending', 'Opening FlowNotes workspace...');
    
    window.location.href = workspaceUrl;
    
    setTimeout(() => {
      updateResult(
        'open-workspace',
        'unknown',
        'Check if Cursor opened workspace',
        workspaceUrl
      );
    }, 1000);
  };

  // Test 3: Open folder
  const testOpenFolder = () => {
    const folderUrl = 'cursor://file/Users/rowan/hopwork/clients';
    updateResult('open-folder', 'pending', 'Opening clients folder...');
    
    window.location.href = folderUrl;
    
    setTimeout(() => {
      updateResult(
        'open-folder',
        'unknown',
        'Check if Cursor opened folder',
        folderUrl
      );
    }, 1000);
  };

  // Test 4: Generate test workspace file
  const generateTestWorkspace = async () => {
    updateResult('generate', 'pending', 'Generating test workspace...');
    
    try {
      const response = await fetch('/api/workspace/generate-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          todoId: '9999999999',
          projectName: 'Test Project',
          gitPath: 'test-repo'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedWorkspace(data.workspacePath);
        updateResult(
          'generate',
          'success',
          `Workspace generated: ${data.workspacePath}`,
          `cursor "${data.workspacePath}"`
        );
      } else {
        updateResult(
          'generate',
          'failed',
          'API endpoint not implemented yet (expected for POC)'
        );
      }
    } catch (err) {
      updateResult(
        'generate',
        'failed',
        'API endpoint not implemented yet (expected for POC)'
      );
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'unknown':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-muted animate-pulse" />;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-6 bg-sidebar">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <h1 className="text-2xl font-bold mb-2">Cursor Workspace Generator Test</h1>
        <p className="text-sm text-muted-foreground">
          Testing Cursor URL protocol and dynamic workspace generation
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          
          {/* Test Panel */}
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-lg font-semibold mb-4">URL Protocol Tests</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Click each button to test if Cursor URL protocol works from browser.
              You'll need to manually verify if Cursor opens.
            </p>

            <div className="space-y-3">
              <Button onClick={testOpenFile} className="w-full justify-start" variant="outline">
                <FileCode className="w-4 h-4 mr-2" />
                Test 1: Open README.md
              </Button>

              <Button onClick={testOpenWorkspace} className="w-full justify-start" variant="outline">
                <FileCode className="w-4 h-4 mr-2" />
                Test 2: Open Workspace File
              </Button>

              <Button onClick={testOpenFolder} className="w-full justify-start" variant="outline">
                <FileCode className="w-4 h-4 mr-2" />
                Test 3: Open Folder
              </Button>

              <Button onClick={generateTestWorkspace} className="w-full justify-start" variant="outline">
                <Terminal className="w-4 h-4 mr-2" />
                Test 4: Generate Workspace (API test)
              </Button>
            </div>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="border rounded-lg p-6 bg-card">
              <h2 className="text-lg font-semibold mb-4">Test Results</h2>
              
              <div className="space-y-3">
                {results.map((result) => (
                  <div
                    key={result.test}
                    className="border rounded-lg p-4 bg-muted/30"
                  >
                    <div className="flex items-start gap-3">
                      {getStatusIcon(result.status)}
                      <div className="flex-1">
                        <div className="font-medium mb-1">{result.test}</div>
                        <div className="text-sm text-muted-foreground mb-2">
                          {result.message}
                        </div>
                        
                        {result.command && (
                          <div className="mt-2 p-2 bg-background rounded border">
                            <div className="flex items-center gap-2">
                              <code className="text-xs flex-1 font-mono">
                                {result.command}
                              </code>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(result.command!)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Test Instructions */}
          <div className="border rounded-lg p-6 bg-muted/20">
            <h2 className="text-lg font-semibold mb-3">Manual Test Instructions</h2>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                <strong>What to verify:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Does clicking the button open Cursor?</li>
                <li>Does it open the correct file/folder/workspace?</li>
                <li>Or does nothing happen (protocol not registered)?</li>
              </ul>
              
              <p className="mt-4">
                <strong>Expected behavior:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>✅ Cursor launches and opens the specified path</li>
                <li>⚠️ Browser asks for permission to open Cursor</li>
                <li>❌ Nothing happens (protocol not supported)</li>
              </ul>

              <p className="mt-4">
                <strong>Fallback if protocol doesn't work:</strong>
              </p>
              <p className="ml-2 mt-1">
                Show copy-to-clipboard button with command: <code className="text-xs">cursor "path/to/workspace"</code>
              </p>
            </div>
          </div>

          {/* Sample Workspace JSON */}
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-lg font-semibold mb-3">Sample Generated Workspace</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Example of what the generated .code-workspace file would look like:
            </p>
            
            <pre className="text-xs bg-background p-4 rounded border overflow-auto">
{`{
  "folders": [
    {
      "name": "🎯 Task: EE7 Upgrade",
      "path": "/Users/rowan/hopwork/clients/vanderbilt-cup-races"
    },
    {
      "name": "📋 Task Context",
      "path": "/Users/rowan/Documents/flownotes-content/clients/vanderbilt-cup-races/todos/9361205578"
    },
    {
      "name": "📖 EE Upgrade Checklist",
      "path": "/Users/rowan/Documents/flownotes/docs/skills/ee-upgrade.md"
    },
    {
      "name": "📖 Git Workflow",
      "path": "/Users/rowan/Documents/flownotes/docs/skills/git-workflow.md"
    }
  ],
  "settings": {
    "flownotes.taskMode": true,
    "flownotes.todoId": "9361205578",
    "flownotes.project": "Vanderbilt Cup Races",
    "flownotes.mcp.enabled": true,
    "git.openRepositoryInParentFolders": "always"
  },
  "extensions": {
    "recommendations": [
      "flownotes.mcp-tools",
      "eamodio.gitlens"
    ]
  }
}`}
            </pre>
          </div>

          {/* Next Steps */}
          <div className="border rounded-lg p-6 bg-primary/5 border-primary/20">
            <h2 className="text-lg font-semibold mb-3">Next Steps</h2>
            <div className="text-sm space-y-2">
              <p>
                <strong>If tests pass:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                <li>Create database migration for project git mappings</li>
                <li>Implement workspace generator service</li>
                <li>Create API endpoint: POST /api/workspace/generate</li>
                <li>Add "Open in Cursor" button to document viewer</li>
              </ul>
              
              <p className="mt-4">
                <strong>If tests fail:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                <li>Use copy-to-clipboard fallback pattern</li>
                <li>Show terminal command: <code className="text-xs">cursor "workspace-path"</code></li>
                <li>Still generate workspace files for manual opening</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
